import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService, capatazService, proyectoService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import '../styles/Perfil.css';

const Perfil = () => {
  const { usuario: usuarioAuth, esContratista, esCapataz, actualizarUsuario } = useAuth();
  const navigate = useNavigate();
  const [perfil, setPerfil] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editando, setEditando] = useState(false);
  const [invitaciones, setInvitaciones] = useState([]);
  const [asociaciones, setAsociaciones] = useState([]);
  const [formData, setFormData] = useState({
    nombre: '',
    telefono: '',
    nombre_empresa: '',
    logo_url: '',
    direccion_empresa: '',
    especialidad: ''
  });

  useEffect(() => {
    cargarPerfil();
  }, []);

  const cargarPerfil = async () => {
    try {
      const response = await authService.getProfile();
      setPerfil(response.data);
      setFormData({
        nombre: response.data.nombre || '',
        telefono: response.data.telefono || '',
        nombre_empresa: response.data.nombre_empresa || '',
        logo_url: response.data.logo_url || '',
        direccion_empresa: response.data.direccion_empresa || '',
        especialidad: response.data.especialidad || ''
      });

      // Si es capataz, cargar invitaciones y asociaciones
      if (esCapataz) {
        try {
          const [invitacionesRes, asociacionesRes] = await Promise.all([
            capatazService.getInvitaciones(),
            capatazService.getAsociaciones()
          ]);
          setInvitaciones(invitacionesRes.data);
          setAsociaciones(asociacionesRes.data);
        } catch (error) {
          console.error('Error al cargar invitaciones/asociaciones:', error);
        }
      }
    } catch (error) {
      console.error('Error al cargar perfil:', error);
      alert('Error al cargar perfil');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await authService.updateProfile(formData);
      setPerfil(response.data.usuario);
      actualizarUsuario(response.data.usuario);
      setEditando(false);
      alert('Perfil actualizado exitosamente');
    } catch (error) {
      console.error('Error al actualizar perfil:', error);
      alert(error.response?.data?.error || 'Error al actualizar perfil');
    }
  };

  const cancelarEdicion = () => {
    setFormData({
      nombre: perfil.nombre || '',
      telefono: perfil.telefono || '',
      nombre_empresa: perfil.nombre_empresa || '',
      logo_url: perfil.logo_url || '',
      direccion_empresa: perfil.direccion_empresa || '',
      especialidad: perfil.especialidad || ''
    });
    setEditando(false);
  };

  const handleAceptarInvitacion = async (contratistaId, contratistaName) => {
    if (!window.confirm(`¿Aceptar la invitación de ${contratistaName}?`)) return;

    try {
      await capatazService.aceptarInvitacion(contratistaId);
      alert(`Invitación de ${contratistaName} aceptada exitosamente.`);
      cargarPerfil();
    } catch (error) {
      console.error('Error al aceptar invitación:', error);
      alert(error.response?.data?.error || 'Error al aceptar invitación');
    }
  };

  const handleRechazarInvitacion = async (contratistaId, contratistaName) => {
    if (!window.confirm(`¿Rechazar la invitación de ${contratistaName}?`)) return;

    try {
      await capatazService.rechazarInvitacion(contratistaId);
      alert('Invitación rechazada');
      cargarPerfil();
    } catch (error) {
      console.error('Error al rechazar invitación:', error);
      alert(error.response?.data?.error || 'Error al rechazar invitación');
    }
  };

  const handleAprobarProyecto = async (proyectoId, nombreProyecto) => {
    if (!window.confirm(`¿Aprobar el proyecto "${nombreProyecto}"? El capataz podrá verlo y registrar avances.`)) return;

    try {
      await proyectoService.aprobar(proyectoId);
      alert('Proyecto aprobado exitosamente');
      cargarPerfil();
    } catch (error) {
      console.error('Error al aprobar proyecto:', error);
      alert(error.response?.data?.error || 'Error al aprobar proyecto');
    }
  };

  if (loading) {
    return <div className="loading">Cargando perfil...</div>;
  }

  if (!perfil) {
    return <div className="loading">Error al cargar perfil</div>;
  }

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
  const BASE_URL = API_URL.replace('/api', '');

  return (
    <div className="perfil-container">
      <div className="page-header">
        <h1>Mi Perfil</h1>
        {!editando && (
          <button className="btn btn-primary" onClick={() => setEditando(true)}>
            Editar Perfil
          </button>
        )}
      </div>

      <div className="perfil-content">
        {/* Información General */}
        <div className="perfil-card">
          <h2>Información General</h2>

          {editando ? (
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Nombre Completo *</label>
                <input
                  type="text"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  required
                  placeholder="Tu nombre completo"
                />
              </div>

              <div className="form-group">
                <label>Teléfono</label>
                <input
                  type="tel"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleChange}
                  placeholder="Ej: +56912345678"
                />
              </div>

              {esContratista && (
                <>
                  <div className="form-group">
                    <label>Nombre de la Empresa</label>
                    <input
                      type="text"
                      name="nombre_empresa"
                      value={formData.nombre_empresa}
                      onChange={handleChange}
                      placeholder="Nombre de tu empresa"
                    />
                  </div>

                  <div className="form-group">
                    <label>Logo URL</label>
                    <input
                      type="text"
                      name="logo_url"
                      value={formData.logo_url}
                      onChange={handleChange}
                      placeholder="URL del logo de tu empresa"
                    />
                  </div>

                  <div className="form-group">
                    <label>Dirección de la Empresa</label>
                    <textarea
                      name="direccion_empresa"
                      value={formData.direccion_empresa}
                      onChange={handleChange}
                      rows="3"
                      placeholder="Dirección completa de la empresa"
                    />
                  </div>
                </>
              )}

              {esCapataz && (
                <div className="form-group">
                  <label>Especialidad</label>
                  <input
                    type="text"
                    name="especialidad"
                    value={formData.especialidad}
                    onChange={handleChange}
                    placeholder="Ej: Electricidad, Construcción, Plomería"
                  />
                </div>
              )}

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={cancelarEdicion}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  Guardar Cambios
                </button>
              </div>
            </form>
          ) : (
            <div className="info-display">
              <div className="info-item">
                <strong>Email:</strong>
                <span>{perfil.email}</span>
              </div>
              <div className="info-item">
                <strong>Nombre:</strong>
                <span>{perfil.nombre}</span>
              </div>
              <div className="info-item">
                <strong>Teléfono:</strong>
                <span>{perfil.telefono || 'No especificado'}</span>
              </div>
              <div className="info-item">
                <strong>Tipo de Usuario:</strong>
                <span className="badge-tipo">{perfil.tipo === 'contratista' ? 'Contratista' : 'Capataz'}</span>
              </div>
              <div className="info-item">
                <strong>Fecha de Registro:</strong>
                <span>{new Date(perfil.created_at).toLocaleDateString()}</span>
              </div>

              {esContratista && (
                <>
                  {perfil.nombre_empresa && (
                    <div className="info-item">
                      <strong>Empresa:</strong>
                      <span>{perfil.nombre_empresa}</span>
                    </div>
                  )}
                  {perfil.direccion_empresa && (
                    <div className="info-item">
                      <strong>Dirección Empresa:</strong>
                      <span>{perfil.direccion_empresa}</span>
                    </div>
                  )}
                </>
              )}

              {esCapataz && perfil.especialidad && (
                <div className="info-item">
                  <strong>Especialidad:</strong>
                  <span className="especialidad-badge">{perfil.especialidad}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Logo de la Empresa (solo para contratistas) */}
        {esContratista && perfil.logo_url && (
          <div className="perfil-card logo-card">
            <h2>Logo de la Empresa</h2>
            <div className="logo-preview">
              <img src={perfil.logo_url} alt="Logo de la empresa" />
            </div>
          </div>
        )}

        {/* Información de Capataz (solo para capataces) */}
        {esCapataz && (
          <>
            <div className="perfil-card qr-card">
              <h2>Código QR de Asociación</h2>
              <p className="qr-description">
                Muestra este código QR a un contratista para que pueda asociarte a su empresa.
              </p>

              <div className="qr-container">
                {perfil.qr_code_url && (
                  <img src={perfil.qr_code_url} alt="Código QR" className="qr-code" />
                )}
              </div>

              <div className="codigo-unico-container">
                <strong>Código Único:</strong>
                <div className="codigo-unico">{perfil.codigo_unico}</div>
                <p className="codigo-help">
                  El contratista también puede usar este código para buscarte manualmente.
                </p>
              </div>
            </div>

            {/* Invitaciones Pendientes */}
            {invitaciones.length > 0 && (
              <div className="perfil-card invitaciones-card">
                <h2>📩 Invitaciones Pendientes ({invitaciones.length})</h2>
                <p className="card-descripcion">
                  Tienes {invitaciones.length} invitación{invitaciones.length !== 1 ? 'es' : ''} de contratistas que desean trabajar contigo.
                </p>

                <div className="invitaciones-lista">
                  {invitaciones.map((inv) => (
                    <div key={inv.invitacion_id} className="invitacion-item">
                      <div className="invitacion-header">
                        <div className="contratista-info-inv">
                          <h4>{inv.contratista_empresa || inv.contratista_nombre}</h4>
                          <p className="contratista-detalle">{inv.contratista_email}</p>
                          <p className="fecha-invitacion">
                            Invitación recibida: {new Date(inv.fecha_invitacion).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="invitacion-acciones">
                        <button
                          className="btn btn-success btn-sm"
                          onClick={() => handleAceptarInvitacion(inv.contratista_id, inv.contratista_empresa || inv.contratista_nombre)}
                        >
                          ✓ Aceptar
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleRechazarInvitacion(inv.contratista_id, inv.contratista_empresa || inv.contratista_nombre)}
                        >
                          ✗ Rechazar
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Asociaciones Activas */}
            <div className="perfil-card asociaciones-card">
              <h2>🤝 Mis Contratistas</h2>

              {asociaciones.length > 0 ? (
                <>
                  <p className="card-descripcion">
                    Estás trabajando con {asociaciones.length} contratista{asociaciones.length !== 1 ? 's' : ''}.
                  </p>
                  <div className="asociaciones-lista">
                    {asociaciones.map((asoc) => (
                      <div key={asoc.asociacion_id} className="asociacion-item">
                        <div className="asociacion-header">
                          <div className="contratista-info-asoc">
                            <h4>{asoc.contratista_empresa || asoc.contratista_nombre}</h4>
                            <p className="contratista-detalle">
                              <strong>Email:</strong> {asoc.contratista_email}
                            </p>
                            {asoc.contratista_telefono && (
                              <p className="contratista-detalle">
                                <strong>Teléfono:</strong> {asoc.contratista_telefono}
                              </p>
                            )}
                            <p className="fecha-asociacion">
                              ✓ Asociado desde: {new Date(asoc.fecha_aceptacion).toLocaleDateString()}
                            </p>
                          </div>
                          <span className="badge-asociacion-activa">Activo</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="no-asociacion">
                  {invitaciones.length > 0
                    ? 'Acepta una invitación para comenzar a trabajar con un contratista.'
                    : 'No estás asociado a ningún contratista. Comparte tu código QR o código único para recibir invitaciones.'}
                </p>
              )}
            </div>
          </>
        )}

        {/* Proyectos (para ambos tipos de usuario) */}
        {perfil.proyectos && perfil.proyectos.length > 0 && (
          <div className="perfil-card proyectos-card">
            <div className="card-header">
              <h2>{esContratista ? 'Mis Proyectos Recientes' : 'Proyectos Asignados'}</h2>
              <button
                className="btn btn-sm btn-primary"
                onClick={() => navigate('/proyectos')}
              >
                Ver Todos
              </button>
            </div>

            <div className="proyectos-lista">
              {perfil.proyectos.map(proyecto => (
                <div
                  key={proyecto.id}
                  className={`proyecto-item ${proyecto.estado === 'planificacion' ? 'no-clickable' : ''}`}
                  onClick={() => proyecto.estado !== 'planificacion' && navigate(`/proyectos/${proyecto.id}`)}
                >
                  <div className="proyecto-item-header">
                    <div>
                      <h4>{proyecto.nombre}</h4>
                      <p className="proyecto-numero">{proyecto.numero_proyecto}</p>
                    </div>
                    <span className={`estado-badge ${proyecto.estado}`}>
                      {proyecto.estado.replace('_', ' ')}
                    </span>
                  </div>

                  <div className="proyecto-item-info">
                    {proyecto.cliente_nombre && (
                      <p><strong>Cliente:</strong> {proyecto.cliente_nombre}</p>
                    )}
                    {esContratista && proyecto.capataz_nombre && (
                      <p><strong>Capataz:</strong> {proyecto.capataz_nombre}</p>
                    )}
                    {proyecto.direccion && (
                      <p><strong>Dirección:</strong> {proyecto.direccion}</p>
                    )}
                  </div>

                  {proyecto.estado === 'planificacion' && esContratista ? (
                    <div className="proyecto-pendiente-aprobacion">
                      <p className="mensaje-pendiente">⏳ Este proyecto requiere aprobación para que el capataz pueda verlo</p>
                      <button
                        className="btn btn-success btn-aprobar"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAprobarProyecto(proyecto.id, proyecto.nombre);
                        }}
                      >
                        ✓ Aprobar Proyecto
                      </button>
                    </div>
                  ) : (
                    <div className="proyecto-progreso-mini">
                      <div className="progreso-header">
                        <span>Avance</span>
                        <span className="progreso-porcentaje">{proyecto.porcentaje_avance || 0}%</span>
                      </div>
                      <div className="progreso-bar">
                        <div
                          className="progreso-fill"
                          style={{ width: `${proyecto.porcentaje_avance || 0}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Perfil;
